import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axiosInstance from "../../api/axiosInstance";
import { Box, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";

const ClaimedLeads = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const pageFromParams = Math.max(parseInt(searchParams.get("page")) || 1, 1);

  const limitFromParams = Math.min(Math.max(parseInt(searchParams.get("limit")) || 10, 1), 100);

  const [claimedLeads, setClaimedLeads] = useState([]);
  const [loading, setLoading] = useState(false);

  const [pagination, setPagination] = useState({
    page: pageFromParams - 1,
    pageSize: limitFromParams,
    total: 0,
  });

  const getClaimedLeads = async (page, pageSize) => {
    try {
      setLoading(true);

      const apiPage = page + 1;

      const res = await axiosInstance.get("api/webhooks/claim-leads", {
        params: {
          page: apiPage,
          limit: pageSize,
        },
      });

      if (res.status === 200) {
        setClaimedLeads(res.data.data || []);

        const serverPagination = res.data.pagination || {};

        setPagination({
          page: (serverPagination.page || apiPage) - 1,
          pageSize: serverPagination.limit || pageSize,
          total: serverPagination.total || 0,
        });
      }
    } catch (error) {
      console.error("error getting claimed leads:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getClaimedLeads(pagination.page, pagination.pageSize); // eslint-disable-next-line
  }, []);

  const handlePaginationChange = (model) => {
    const nextPage = model.page;
    const nextPageSize = model.pageSize;

    setPagination((prev) => ({
      ...prev,
      page: nextPage,
      pageSize: nextPageSize,
    }));

    setSearchParams({
      page: String(nextPage + 1),
      limit: String(nextPageSize),
    });

    getClaimedLeads(nextPage, nextPageSize);
  };

  const columns = [
    {
      field: "id",
      headerName: "ID",
      width: 80,
    },
    {
      field: "staff_email",
      headerName: "Claimed By",
      flex: 1,
      minWidth: 220,
    },
    {
      field: "full_address",
      headerName: "Property Address",
      flex: 1.5,
      minWidth: 280,
    },
    {
      field: "city",
      headerName: "City",
      flex: 1,
      minWidth: 130,
    },
    {
      field: "state",
      headerName: "State",
      width: 100,
    },
    {
      field: "zip",
      headerName: "ZIP",
      width: 100,
    },
    {
      field: "owner_name",
      headerName: "Owner",
      flex: 1,
      minWidth: 180,
    },
    {
      field: "owner_phone",
      headerName: "Owner Phone",
      flex: 1,
      minWidth: 160,
    },
    {
      field: "claimed_at",
      headerName: "Claimed At",
      flex: 1,
      minWidth: 180,
      valueGetter: (value) => {
        if (!value) return "-";

        return new Date(value).toLocaleString();
      },
    },
  ];

  const rows = claimedLeads.map((lead) => ({
    id: lead.id,

    staff_email: lead.staff_email || "-",

    full_address: lead.full_address || lead.address || "-",

    city: lead.city || "-",

    state: lead.state || "-",

    zip: lead.zip || "-",

    owner_name: lead.owner_name || `${lead.owner_first_name || ""} ${lead.owner_last_name || ""}`.trim() || "-",

    owner_phone: lead.owner_phone || "-",

    claimed_at: lead.claimed_at || null,
  }));

  return (
    <Box sx={{ width: "100%", p: 3 }}>
      <Typography
        variant="h5"
        sx={{
          mb: 3,
          fontWeight: 600,
        }}
      >
        Claimed Leads
      </Typography>

      <Box sx={{ width: "100%" }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          autoHeight
          pagination
          paginationMode="server"
          rowCount={pagination.total}
          paginationModel={{
            page: pagination.page,
            pageSize: pagination.pageSize,
          }}
          onPaginationModelChange={handlePaginationChange}
          pageSizeOptions={[10, 25, 50, 100]}
          disableRowSelectionOnClick
          sx={{
            border: "1px solid #e0e0e0",
            borderRadius: 2,

            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#f3f4f6",
              color: "#374151",
              fontWeight: 600,
            },

            "& .MuiDataGrid-columnHeader": {
              backgroundColor: "#f3f4f6",
            },

            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: 600,
            },

            "& .MuiDataGrid-row:hover": {
              backgroundColor: "#f9fafb",
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default ClaimedLeads;
